import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type {
  GridFieldType,
  NestedListColumnBinding,
  SummaryLayoutItem,
  TableDynamicColumn,
} from '@/features/Inventory/ObjectTypePassportGrid/model/types'

/** Merge catalog + palette-fetched fields by id (palette first, then own fields overwrite). */
export function mergeInventoryFieldsById(
  ownFields: ObjectInventoryField[],
  paletteFields: ObjectInventoryField[],
): ObjectInventoryField[] {
  const map = new Map<string, ObjectInventoryField>()
  for (const f of paletteFields) map.set(String(f.id), f)
  for (const f of ownFields) map.set(String(f.id), f)
  return [...map.values()]
}

function sameDynamicColumn(
  c: TableDynamicColumn,
  fieldId: string | number,
  nestedBinding?: NestedListColumnBinding | null,
): boolean {
  if (String(c.field_id) !== String(fieldId)) return false
  return JSON.stringify(c.nested_binding ?? null) === JSON.stringify(nestedBinding ?? null)
}

const ACTIONS = 1

/** Drag / segment id for the fixed «Наименование» column (not a `table_columns` row). */
export const PB_TABLE_NAME_SEGMENT_ID = '__pb_name__'

export function defaultTableNameSpan(): number {
  return 12 - ACTIONS - 0
}

/** Sum of inner spans + actions must equal 12. */
export function innerLayoutSum(f: GridFieldType): number {
  const name = f.table_name_span ?? defaultTableNameSpan()
  const mids = f.table_columns ?? []
  return name + mids.reduce((s, c) => s + c.span, 0) + ACTIONS
}

/** Valid visual order of name + dynamic column ids; falls back if stored order is invalid. */
export function buildSegmentOrder(f: GridFieldType): string[] {
  const cols = f.table_columns ?? []
  const defaultOrder = [PB_TABLE_NAME_SEGMENT_ID, ...cols.map((c) => c.id)]
  const o = f.table_segment_order
  if (!o?.length) return defaultOrder
  const dynSet = new Set(cols.map((c) => c.id))
  let nameCount = 0
  const usedDyn = new Set<string>()
  const out: string[] = []
  for (const id of o) {
    if (id === PB_TABLE_NAME_SEGMENT_ID) {
      if (nameCount > 0) return defaultOrder
      nameCount += 1
      out.push(id)
      continue
    }
    if (!dynSet.has(id) || usedDyn.has(id)) return defaultOrder
    usedDyn.add(id)
    out.push(id)
  }
  if (nameCount !== 1) return defaultOrder
  if (out.length !== 1 + cols.length) return defaultOrder
  if (usedDyn.size !== cols.length) return defaultOrder
  return out
}

export function segmentSpans(f: GridFieldType): number[] {
  const order = buildSegmentOrder(f)
  const cols = f.table_columns ?? []
  const colById = new Map(cols.map((c) => [c.id, c]))
  return order.map((id) => {
    if (id === PB_TABLE_NAME_SEGMENT_ID) return f.table_name_span ?? 11
    return colById.get(id)?.span ?? 1
  })
}

function countDynBefore(order: string[], i: number): number {
  return order.slice(0, i).filter((id) => id !== PB_TABLE_NAME_SEGMENT_ID).length
}

export function normalizeTableLayout(f: GridFieldType): GridFieldType {
  if (f.type !== 'table') return f
  /** Inner name+dynamics+actions layout does not apply in «Сводная информация» mode. */
  if (f.summary_mode) return f
  let name = f.table_name_span ?? 11
  const cols = [...(f.table_columns ?? [])].map((c) => ({ ...c, span: Math.max(1, c.span) }))
  let sum = name + cols.reduce((s, c) => s + c.span, 0) + ACTIONS
  while (sum > 12 && name > 1) {
    name -= 1
    sum = name + cols.reduce((s, c) => s + c.span, 0) + ACTIONS
  }
  while (sum < 12) {
    name += 1
    sum = name + cols.reduce((s, c) => s + c.span, 0) + ACTIONS
  }
  const patched = { ...f, table_name_span: name, table_columns: cols }
  const order = buildSegmentOrder(patched)
  return { ...patched, table_segment_order: order }
}

export function addTableColumn(
  f: GridFieldType,
  fieldId: string | number,
  displayName: string,
  insertIndex: number,
  nestedBinding?: NestedListColumnBinding | null,
): GridFieldType {
  if (f.type !== 'table') return f
  if (f.summary_mode) return f
  let nameSpan = f.table_name_span ?? 11
  const cols = [...(f.table_columns ?? [])]
  if (cols.some((c) => sameDynamicColumn(c, fieldId, nestedBinding))) return f
  if (nameSpan < 2) return f
  const idx = Math.max(0, Math.min(insertIndex, cols.length))
  const col: TableDynamicColumn = {
    id: crypto.randomUUID(),
    field_id: fieldId,
    name: displayName,
    span: 1,
    ...(nestedBinding != null && nestedBinding.segments.length > 0
      ? { nested_binding: nestedBinding }
      : {}),
  }
  cols.splice(idx, 0, col)
  nameSpan -= 1
  if (!f.table_segment_order?.length) {
    return normalizeTableLayout({ ...f, table_name_span: nameSpan, table_columns: cols })
  }
  const order = buildSegmentOrder(f)
  const dynIds = order.filter((id) => id !== PB_TABLE_NAME_SEGMENT_ID)
  const nameIdx = order.indexOf(PB_TABLE_NAME_SEGMENT_ID)
  const insertAtOrderIndex =
    idx >= dynIds.length
      ? dynIds.length === 0
        ? nameIdx + 1
        : order.indexOf(dynIds[dynIds.length - 1]) + 1
      : order.indexOf(dynIds[idx])
  const nextOrder = [...order]
  nextOrder.splice(insertAtOrderIndex, 0, col.id)
  return normalizeTableLayout({
    ...f,
    table_name_span: nameSpan,
    table_columns: cols,
    table_segment_order: nextOrder,
  })
}

export function removeTableColumn(f: GridFieldType, columnId: string): GridFieldType {
  if (f.type !== 'table') return f
  if (f.summary_mode) return f
  const cols = [...(f.table_columns ?? [])]
  const i = cols.findIndex((c) => String(c.id) === String(columnId))
  if (i === -1) return f
  const [removed] = cols.splice(i, 1)
  const nameSpan = (f.table_name_span ?? 11) + removed.span
  const order = buildSegmentOrder(f).filter((id) => String(id) !== String(columnId))
  return normalizeTableLayout({ ...f, table_name_span: nameSpan, table_columns: cols, table_segment_order: order })
}

/** Renames a dynamic column label in the builder (stored in `table_columns[].name`; binding `field_id` unchanged). */
export function renameTableColumn(f: GridFieldType, columnId: string, name: string): GridFieldType {
  if (f.type !== 'table') return f
  if (f.summary_mode) return f
  const cols = [...(f.table_columns ?? [])]
  const i = cols.findIndex((c) => String(c.id) === String(columnId))
  if (i === -1) return f
  const prev = cols[i].name
  const trimmed = name.trim()
  const nextName = trimmed || prev
  if (nextName === prev) return f
  const nextCols = [...cols]
  nextCols[i] = { ...nextCols[i], name: nextName }
  return normalizeTableLayout({ ...f, table_columns: nextCols })
}

export function reorderTableSegments(f: GridFieldType, fromIndex: number, toIndex: number): GridFieldType {
  if (f.type !== 'table') return f
  if (f.summary_mode) return f
  const order = buildSegmentOrder(f)
  if (fromIndex < 0 || fromIndex >= order.length) return f
  if (toIndex < 0 || toIndex >= order.length) return f
  if (fromIndex === toIndex) return f
  const next = [...order]
  const [m] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, m)
  return normalizeTableLayout({ ...f, table_segment_order: next })
}

/**
 * Resize between two adjacent flexible segments (name at 0, dynamics at 1..n). Not for pair ending at actions.
 * `pairIndex`: left segment index. `deltaLeft`: positive grows left, shrinks right (in 1/12 units).
 */
export function resizeAdjacentPair(f: GridFieldType, pairIndex: number, deltaLeft: number): GridFieldType {
  if (f.type !== 'table') return f
  if (f.summary_mode) return f
  const order = buildSegmentOrder(f)
  const nameSpan = f.table_name_span ?? 11
  const cols = [...(f.table_columns ?? [])]
  const colById = new Map(cols.map((c) => [c.id, { ...c }]))
  const spans = segmentSpans(f)
  if (pairIndex < 0 || pairIndex >= spans.length - 1) return f
  const d = Math.trunc(deltaLeft)
  if (d === 0) return f
  const maxGrowLeft = spans[pairIndex + 1] - 1
  const maxShrinkLeft = spans[pairIndex] - 1
  const actual = d > 0 ? Math.min(d, maxGrowLeft) : -Math.min(-d, maxShrinkLeft)
  if (actual === 0) return f
  const next = [...spans]
  next[pairIndex] += actual
  next[pairIndex + 1] -= actual
  let newName = nameSpan
  for (let i = 0; i < order.length; i++) {
    const id = order[i]
    const span = next[i]
    if (id === PB_TABLE_NAME_SEGMENT_ID) {
      newName = span
    } else {
      const col = colById.get(id)
      if (col) col.span = span
    }
  }
  const newCols = cols.map((c) => {
    const x = colById.get(c.id)
    return x ? { ...c, span: x.span } : c
  })
  const out = { ...f, table_name_span: newName, table_columns: newCols }
  return innerLayoutSum(out) === 12 ? out : f
}

/** Strip «[Перечень] » prefix from palette drag label. */
export function shortFieldLabelFromDragName(fullName: string): string {
  const t = fullName.trim()
  const m = t.match(/^\[[^\]]+\]\s*(.+)$/)
  return (m?.[1] ?? t).trim() || t
}

/**
 * Where to insert a new dynamic column (0 = first after «Наименование» when name is first, etc.).
 * `x` is relative to the table body left edge.
 */
export function insertIndexFromPointer(x: number, width: number, f: GridFieldType): number {
  if (width <= 0 || f.type !== 'table') return 0
  if (f.summary_mode) return 0
  const order = buildSegmentOrder(f)
  const cols = f.table_columns ?? []
  const colById = new Map(cols.map((c) => [c.id, c]))
  const flexSegs = order.map((id) => {
    if (id === PB_TABLE_NAME_SEGMENT_ID) return f.table_name_span ?? 11
    return colById.get(id)?.span ?? 1
  })
  const segs = [...flexSegs, ACTIONS]
  const r = Math.max(0, Math.min(12, (x / width) * 12))
  let a = 0
  for (let i = 0; i < segs.length; i++) {
    const b = a + segs[i]
    const inSeg = r <= b || i === segs.length - 1
    if (inSeg) {
      const localMid = (a + b) / 2
      if (i === segs.length - 1) return cols.length
      if (order[i] === PB_TABLE_NAME_SEGMENT_ID) {
        if (cols.length === 0) return 0
        const dynBefore = countDynBefore(order, i)
        return r <= localMid ? dynBefore : dynBefore + 1
      }
      const di = countDynBefore(order, i)
      return r <= localMid ? di : di + 1
    }
    a = b
  }
  return cols.length
}

const SUMMARY_MIN_SPAN = 1
const SUMMARY_DEFAULT_SPAN = 4

/**
 * Store palette/API field id in `summary_layout[].ref_id` without coercing nanoids to NaN.
 * Pure numeric strings become numbers for backward compatibility with older saved JSON.
 */
function normalizeSummaryRefId(fieldId: string | number): string | number {
  if (typeof fieldId === 'number') {
    return Number.isFinite(fieldId) ? fieldId : String(fieldId)
  }
  const s = String(fieldId).trim()
  if (s === '') return s
  const n = Number(s)
  if (Number.isFinite(n) && String(n) === s) return n
  return s
}

/** Insert index (0..n) when dropping into a horizontal summary strip with `itemCount` items. */
export function insertIndexSummaryFromPointer(x: number, width: number, itemCount: number): number {
  if (width <= 0) return itemCount
  const slots = itemCount + 1
  const t = Math.max(0, Math.min(1, x / width))
  return Math.min(itemCount, Math.floor(t * slots))
}

export function packSummaryLayoutRows(layout: SummaryLayoutItem[]): SummaryLayoutItem[][] {
  const rows: SummaryLayoutItem[][] = []
  let cur: SummaryLayoutItem[] = []
  let sum = 0
  for (const it of layout) {
    const len = Math.min(12, Math.max(SUMMARY_MIN_SPAN, it.length))
    if (sum + len > 12 && cur.length) {
      rows.push(cur)
      cur = []
      sum = 0
    }
    cur.push(it)
    sum += len
  }
  if (cur.length) rows.push(cur)
  return rows.length ? rows : [[]]
}

export function addSummaryLayoutItemFromPalette(
  f: GridFieldType,
  fieldId: string | number,
  insertIndex: number,
  nestedBinding?: NestedListColumnBinding | null,
  displayLabel?: string,
): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  const layout = [...(f.summary_layout ?? [])]
  const id = crypto.randomUUID()
  const refId = normalizeSummaryRefId(fieldId)
  const trimmedLabel = displayLabel?.trim()
  const item: SummaryLayoutItem = {
    id,
    kind: 'field',
    ref_id: refId,
    length: SUMMARY_DEFAULT_SPAN,
    ...(trimmedLabel ? { label: trimmedLabel } : {}),
    ...(nestedBinding != null && nestedBinding.segments?.length
      ? { nested_binding: nestedBinding }
      : {}),
  }
  const idx = Math.max(0, Math.min(insertIndex, layout.length))
  layout.splice(idx, 0, item)
  return { ...f, summary_layout: layout }
}

export function removeSummaryLayoutItem(f: GridFieldType, itemId: string): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  const layout = (f.summary_layout ?? []).filter((x) => String(x.id) !== String(itemId))
  return { ...f, summary_layout: layout }
}

export function updateSummaryLayoutItemLength(f: GridFieldType, itemId: string, length: number): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  const next = Math.max(SUMMARY_MIN_SPAN, Math.min(12, length))
  const layout = (f.summary_layout ?? []).map((x) =>
    String(x.id) === String(itemId) ? { ...x, length: next } : x,
  )
  return { ...f, summary_layout: layout }
}

export function reorderSummaryLayoutItems(f: GridFieldType, fromIndex: number, toIndex: number): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  const layout = [...(f.summary_layout ?? [])]
  if (fromIndex < 0 || fromIndex >= layout.length) return f
  if (toIndex < 0 || toIndex > layout.length) return f
  if (fromIndex === toIndex) return f
  const [m] = layout.splice(fromIndex, 1)
  const adj = fromIndex < toIndex ? toIndex - 1 : toIndex
  layout.splice(Math.max(0, Math.min(adj, layout.length)), 0, m)
  return { ...f, summary_layout: layout }
}

/** Add a summary card from an inner table header drag (`table-column`). «Наименование» uses `nameColumnRefId`. */
export function addSummaryFromTableColumnSegment(
  f: GridFieldType,
  columnSegmentId: string,
  insertIndex: number,
  nameColumnRefId: string | number | null,
  nameColumnDisplayLabel?: string | null,
): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  if (columnSegmentId === PB_TABLE_NAME_SEGMENT_ID) {
    if (nameColumnRefId == null) return f
    const nl = nameColumnDisplayLabel?.trim()
    return addSummaryLayoutItemFromPalette(f, nameColumnRefId, insertIndex, null, nl || undefined)
  }
  const col = f.table_columns?.find((c) => String(c.id) === String(columnSegmentId))
  if (!col) return f
  const colLabel = col.name?.trim()
  return addSummaryLayoutItemFromPalette(
    f,
    col.field_id,
    insertIndex,
    col.nested_binding ?? null,
    colLabel || undefined,
  )
}

/**
 * Drops summary cards whose `ref_id` no longer exists on a field in the same LIST `list_group_id`.
 * Headings (`kind: heading`) are kept until heading placement is implemented.
 */
export function updateSummaryLayoutItemLabel(
  f: GridFieldType,
  itemId: string,
  label: string | undefined,
): GridFieldType {
  if (f.type !== 'table' || !f.summary_mode) return f
  const trimmed = label?.trim()
  const layout = (f.summary_layout ?? []).map((x) => {
    if (String(x.id) !== String(itemId)) return x
    if (!trimmed) {
      const { label: _removed, ...rest } = x
      return rest
    }
    return { ...x, label: trimmed }
  })
  return { ...f, summary_layout: layout }
}

export function pruneSummaryLayoutOrphansForTable(
  grid: GridFieldType[],
  fieldsList: ObjectInventoryField[],
): GridFieldType[] {
  return grid.map((item) => {
    if (item.type !== 'table' || !item.summary_mode || !item.summary_layout?.length) return item
    const gid = item.list_group_id
    if (gid == null) return item
    /** LIST summary cards reference child-type fields; match `id` and optional `inventory_field_id` (drag uses `inventory_field_id ?? id`). */
    const allFieldIds = new Set<string>()
    for (const f of fieldsList) {
      allFieldIds.add(String(f.id))
      const inv = (f as { inventory_field_id?: string | number }).inventory_field_id
      if (inv != null && String(inv).trim() !== '') allFieldIds.add(String(inv))
    }
    const next = item.summary_layout.filter((x) => {
      if (x.kind === 'heading') return true
      return allFieldIds.has(String(x.ref_id))
    })
    if (next.length === item.summary_layout.length) return item
    return { ...item, summary_layout: next }
  })
}
